/**
 * The segment endpoints — slice 2's whole client surface.
 *
 *   listSegments         GET    /segments               list, with counts per row
 *   fetchSegment         GET    /segments/:id           one segment, its datasets, its counts
 *   listSegmentContacts  GET    /segments/:id/contacts  who is actually in it
 *   createSegment        POST   /segments               name, description, dataset ids
 *   updateSegment        PATCH  /segments/:id           rename, re-describe, re-scope
 *   deleteSegment        DELETE /segments/:id
 *
 * Slice 67 adds the two a RULE segment needs:
 *
 *   createRuleSegment         POST /segments                         { type: 'rule', name, description, rules }
 *   fetchMembershipEvents     GET  /segments/:id/membership-events   who entered and left, and why (masked)
 *
 * A rule segment's rules change through `updateSegment` like any other edit
 * (`rules` in the PATCH body), and the API refreshes its members in the same
 * request, so the answer already carries the new counts. The builder's live
 * preview is `lib/api/rules.ts`, because it saves nothing and is not about a
 * segment.
 *
 * Two things worth knowing before calling any of them.
 *
 * **Every read carries the counts.** There is no "give me a segment without its
 * counts" and no separate counts call, because a segment that advertises one
 * number and sends another is the failure this slice exists to prevent. The
 * counts are computed server-side on every read, so nothing here caches one.
 *
 * **Membership is replaced, not patched.** `updateSegment` takes the full
 * desired set of dataset ids: omitting `datasetIds` leaves membership alone,
 * sending `[]` empties the segment. That is deliberate — the control driving it
 * is a column of checkboxes, and a set of checkboxes should send a set.
 *
 * Request bodies are re-validated here against the same contract the API
 * validates against, so a body it could never accept never leaves the browser.
 */
import {
  contactListResponseSchema,
  createRuleSegmentRequestSchema,
  createSegmentRequestSchema,
  okResponseSchema,
  segmentListResponseSchema,
  segmentMembershipEventsResponseSchema,
  segmentResponseSchema,
  updateSegmentRequestSchema,
  type ContactListQuery,
  type ContactListResponse,
  type CreateRuleSegmentRequest,
  type CreateSegmentRequest,
  type OkResponse,
  type Segment,
  type SegmentListQuery,
  type SegmentListResponse,
  type SegmentMembershipEvent,
  type UpdateSegmentRequest,
} from '@wend/shared';
import type { z } from 'zod';

import { apiRequest } from './client';

/** Ids come from the API, but a path segment is never interpolated raw. */
function segmentPath(segmentId: string, suffix = ''): string {
  return `/segments/${encodeURIComponent(segmentId)}${suffix}`;
}

export async function listSegments(
  query: SegmentListQuery,
  signal?: AbortSignal,
): Promise<SegmentListResponse> {
  return apiRequest({
    path: '/segments',
    query: {
      page: String(query.page),
      pageSize: String(query.pageSize),
      sort: query.sort,
      direction: query.direction,
      // An empty search is not a filter for "nothing"; it is no filter at all.
      ...(query.search === undefined || query.search === '' ? {} : { search: query.search }),
    },
    schema: segmentListResponseSchema,
    signal,
  });
}

/** One segment, its member datasets, and the three counts as of right now. */
export async function fetchSegment(segmentId: string, signal?: AbortSignal): Promise<Segment> {
  const response = await apiRequest({
    path: segmentPath(segmentId),
    schema: segmentResponseSchema,
    signal,
  });
  return response.segment;
}

/**
 * A page of the contacts in a segment.
 *
 * These are **rows**, not de-duplicated recipients: it answers "show me the
 * data", while `segment.counts.uniqueRecipients` answers "how many messages".
 * The shape is the slice-1 contact list verbatim, so the same table renders it.
 */
export async function listSegmentContacts(
  segmentId: string,
  query: Omit<ContactListQuery, 'datasetId'>,
  signal?: AbortSignal,
): Promise<ContactListResponse> {
  return apiRequest({
    path: segmentPath(segmentId, '/contacts'),
    query: {
      page: String(query.page),
      pageSize: String(query.pageSize),
      sort: query.sort,
      direction: query.direction,
      ...(query.search === undefined || query.search === '' ? {} : { search: query.search }),
    },
    schema: contactListResponseSchema,
    signal,
  });
}

export async function createSegment(request: CreateSegmentRequest): Promise<Segment> {
  const response = await apiRequest({
    path: '/segments',
    method: 'POST',
    body: createSegmentRequestSchema.parse(request),
    schema: segmentResponseSchema,
  });
  return response.segment;
}

/**
 * Renames, re-describes or re-scopes a segment — any subset of the three.
 *
 * `datasetIds` replaces membership wholesale. The answer is the segment as it
 * now is, counts included, so a caller never has to re-read to find out what
 * the change did to the numbers.
 */
export async function updateSegment(
  segmentId: string,
  request: UpdateSegmentRequest,
): Promise<Segment> {
  const response = await apiRequest({
    path: segmentPath(segmentId),
    method: 'PATCH',
    body: updateSegmentRequestSchema.parse(request),
    schema: segmentResponseSchema,
  });
  return response.segment;
}

/** Deletes the segment. The lists it combined, and their contacts, stay. */
export async function deleteSegment(segmentId: string): Promise<OkResponse> {
  return apiRequest({
    path: segmentPath(segmentId),
    method: 'DELETE',
    schema: okResponseSchema,
  });
}

/**
 * Creates a RULE segment. Its members are computed in the same request (cause
 * `saved`), so the segment page it opens on already has real counts — and the
 * membership timeline already has its first entries.
 */
export async function createRuleSegment(
  request: z.input<typeof createRuleSegmentRequestSchema>,
): Promise<Segment> {
  const body: CreateRuleSegmentRequest = createRuleSegmentRequestSchema.parse(request);
  const response = await apiRequest({
    path: '/segments',
    method: 'POST',
    body,
    schema: segmentResponseSchema,
  });
  return response.segment;
}

/**
 * The newest entries and exits of a rule segment, each with its cause. People
 * arrive masked — a name and one masked address — because a timeline is read
 * over somebody's shoulder far more often than a contact table is.
 */
export async function fetchMembershipEvents(
  segmentId: string,
  limit = 50,
  signal?: AbortSignal,
): Promise<SegmentMembershipEvent[]> {
  const response = await apiRequest({
    path: segmentPath(segmentId, '/membership-events'),
    query: { limit: String(limit) },
    schema: segmentMembershipEventsResponseSchema,
    signal,
  });
  return response.events;
}
